import type { FC } from 'react'
import {
  Desktop,
  MediaContextProvider,
  Mobile,
  Tablet
} from '@/components/atoms/media'
import ProductListing from './productListing/productListingRedesign'
import type { ProductListingFetcher } from '@/components/organisms/productListing/productListingFetcher'
import { ListingData } from 'types/listingData'
import { BridgeProduct } from '@/types/bridgeProduct'
import type { ProductCardColors } from '../atoms/productCardPlaceholder'
import type { ProductCardMode } from './productCard/productCardMode'
import { ProductCardActionType } from './productCard/productCard'
import useDevice from 'hooks/useDevice'
import { useAsset } from '@/providers/assetsProvider'

type ProductsContainerProps = {
  data: ListingData<BridgeProduct>
  action?: ProductCardActionType
  fetcher: ProductListingFetcher
  maxWidth?: ProductsContainerResponsiveData<number | string>
  itemsPerRow?: ProductsContainerResponsiveData<number>
  list: string
  colors?: ProductCardColors
  indentText?: boolean
  mode?: ProductCardMode
  useFitIn?: boolean
} & Rest

type ProductsContainerResponsiveData<T> = {
  wide?: T
  desktop: T
  tablet: T
  mobile: T
}

const ProductsContainer: FC<ProductsContainerProps> = ({
  data,
  action,
  fetcher,
  maxWidth,
  list,
  itemsPerRow = { desktop: 4, tablet: 3, mobile: 2 },
  colors,
  indentText,
  mode,
  useFitIn = false
}) => {
  const productListingProps = {
    data,
    products: data,
    action,
    fetcher,
    listData: data,
    productListingFetcher: fetcher,
    maxWidth,
    list,
    colors,
    indentText,
    mode,
    useFitIn
  }

  const { isMobile, isDesktop, isTablet } = useDevice()
  const { useSpeedCurveOpt } = useAsset()

  return (
    <>
      <MediaContextProvider>
        {useSpeedCurveOpt ? (
          <>
            {isDesktop && (
              <ProductListing
                itemsPerRow={itemsPerRow.desktop}
                listingBannerRowProperty="row"
                listingBannerSizeProperty="size"
                listingBannerImageProperty="desktopImage"
                {...productListingProps}
              />
            )}
            {isTablet && (
              <ProductListing
                itemsPerRow={itemsPerRow.tablet}
                listingBannerRowProperty="row"
                listingBannerSizeProperty="size"
                listingBannerImageProperty="desktopImage"
                {...productListingProps}
              />
            )}
            {isMobile && (
              <ProductListing
                itemsPerRow={itemsPerRow.mobile}
                listingBannerRowProperty="mobileRow"
                listingBannerSizeProperty="mobileSize"
                listingBannerImageProperty="mobileImage"
                {...productListingProps}
              />
            )}
          </>
        ) : (
          <>
            <Desktop>
              <ProductListing
                itemsPerRow={itemsPerRow.desktop}
                listingBannerRowProperty="row"
                listingBannerSizeProperty="size"
                listingBannerImageProperty="desktopImage"
                {...productListingProps}
              />
            </Desktop>
            <Tablet>
              <ProductListing
                itemsPerRow={itemsPerRow.tablet}
                listingBannerRowProperty="row"
                listingBannerSizeProperty="size"
                listingBannerImageProperty="desktopImage"
                {...productListingProps}
              />
            </Tablet>
            <Mobile>
              <ProductListing
                itemsPerRow={itemsPerRow.mobile}
                listingBannerRowProperty="mobileRow"
                listingBannerSizeProperty="mobileSize"
                listingBannerImageProperty="mobileImage"
                {...productListingProps}
              />
            </Mobile>
          </>
        )}
      </MediaContextProvider>
    </>
  )
}

export default ProductsContainer
